import CommonIriDisplay from '../application-core/CommonIriDisplay';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import QualityCheckLoader from '../application-core/QualityCheckLoader';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import QualityCheck from '../application-core/QualityCheck';
import { globalStyles as styles } from '../core/style/gstheme';
import { checkResourceEntitlements, getProjectFromGraph } from '../core/utils/utils';
import { EDITING_OPTIONS } from '../application-core/constants';

// IFP check only applies in OWL DL mode:
// https://www.w3.org/TR/owl-ref/#InverseFunctionalProperty-def

const views = [
    "classcycleclashes",
    "classpropertyissues",
    "punning",
    "inversepropertyissues",
    "disjointnessconsistency",
    "symmetricasymmetricinverses",
    "reflexiveirreflexive",
    "symmetricasymmetric",
    "equivalentdisjoint",
    "equivalencyinconsistency",
    "invalidequivalentproperty",
    "invalidequivalentclass",
    "invalidifp",
    "disjointindividuals",
    "differentfromsameas",
    "invalidsameas",
    "invaliddifferentfrom",
    "incompleterestriction"
];

const consistencyReport = [
    {
        prop: "disjointnessInconsistency",
        message: "The following classes are disjoint with themselves."
    },
    {
        prop: "subclassDisjointnessInconsistency",
        message: "The following classes are a subclass of a class and also disjoint with that class."
    },
    {
        prop: "multipleSubclassDisjointnessInconsistency",
        message: "The following classes have multiple subclass definitions that are in conflict via disjointness."
    },
    {
        prop: "disjointnessEquivalenceInconsistency",
        message: "The following classes are both disjoint and equivalent."
    }
];


class OntologyQuality extends React.Component {

    constructor(props) {
        super(props);
        const filteredViews = views
            .filter(view => {
                if (!props.editingOptions) return true;
                if (view === "punning") return !props.editingOptions.includes(EDITING_OPTIONS.PUNNING);
                if (view === "invalidifp") return props.editingOptions.includes(EDITING_OPTIONS.OWL_DL);
                return true;
            });
        this.state = {
            filteredViews
        };
    }

    displayResults = (issues, isLoading, onRefresh) => {
        const { graph, onToggleView, defaultLang, onDisable, data, entitlement } = this.props;
        const disabledFeatures = data.disabledFeatures;
        const isReadOnly = checkResourceEntitlements(data, entitlement);
        return <div>
            <QualityCheck
                dataTest="class-cycle-clashes"
                title="Class Cycle Clashes"
                issues={issues.classcycleclashes}
                isLoading={isLoading}
                isReadOnly={isReadOnly}
                graph={graph}
                defaultLang={defaultLang}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">This means that at some position lower in the class hierarchy a class occurs again. A class should not be a subclass of itself.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve this by removing the class from lower in the class hierarchy.</Typography>
                </div>}
                loadProps={["id", "cyclicSubclass"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-classcycleclashes")}
                onDisable={(disable) => onDisable("classcycleclashes", disable)}
                onRefresh={() => onRefresh("classcycleclashes")}
                errors={issues["classcycleclashes-error"]}
                fromCache={issues["classcycleclashes-time"]}
                issueDisplay={
                    (cc) => cc.cyclicSubclass && cc.cyclicSubclass.map((v, vIndex) => {
                        return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                            <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            <CommonIriDisplay
                                id={v}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                        </div>;
                    })
                }
            />
            <QualityCheck
                dataTest="class-property-issues"
                title="Classes With Properties Where Classes Expected"
                issues={issues.classpropertyissues}
                isLoading={isLoading}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Certain ontology structures expect classes as the value of properties.</Typography>
                    <Typography variant="body2" color="textPrimary">These errors mean that other ontology structures are given as values.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve the issue by removing the problem structures as values for the properties.</Typography>
                </div>}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "classPropertyIssue"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-classpropertyissues")}
                onDisable={(disable) => onDisable("classpropertyissues", disable)}
                onRefresh={() => onRefresh("classpropertyissues")}
                errors={issues["classpropertyissues-error"]}
                fromCache={issues["classpropertyissues-time"]}
                issueDisplay={
                    (cc) => cc.classPropertyIssue && [...cc.classPropertyIssue].sort().map((v, vIndex) => {
                        return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                            <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            <CommonIriDisplay
                                id={v}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                        </div>;
                    })
                }
            />
            {this.state.filteredViews.includes("punning") &&
                <QualityCheck
                    dataTest="punning"
                    title="Resources That Are Punned (Class and Named Individual)"
                    issues={issues.punning}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    defaultLang={defaultLang}
                    loadProps={["id"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Punning happens when resources are both a class and a named individual.</Typography>
                        <Typography variant="body2" color="textPrimary">Whilst this is permitted it can cause unwanted complexity and confusion.</Typography>
                        <Typography variant="body2" color="textPrimary">To resolve this remove the OWL class type in the named individual view.</Typography>
                    </div>}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-punning")}
                    onDisable={(disable) => onDisable("punning", disable)}
                    onRefresh={() => onRefresh("punning")}
                    errors={issues["punning-error"]}
                    fromCache={issues["punning-time"]}
                    issueDisplay={
                        (cc) => cc.conflictingTypes ?
                            <div data-test={"item"} style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={cc.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                            </div>
                            :
                            null
                    }
                />
            }
            <QualityCheck
                dataTest="inverse-property-issues"
                title="Inverse Relationships Where Not Expected"
                issues={issues.inversepropertyissues}
                isLoading={isLoading}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Inverse relationships only make sense between object properties.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve the issue by removing the relationship.</Typography>
                </div>}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "inversePropertyIssue"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-inversepropertyissues")}
                onDisable={(disable) => onDisable("inversepropertyissues", disable)}
                onRefresh={() => onRefresh("inversepropertyissues")}
                errors={issues["inversepropertyissues-error"]}
                fromCache={issues["inversepropertyissues-time"]}
                issueDisplay={
                    (cc) => cc.inversePropertyIssue && [...cc.inversePropertyIssue].sort().map((v, vIndex) => {
                        return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                            <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            <CommonIriDisplay
                                id={v}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                        </div>;
                    })
                }
            />
            <QualityCheck
                dataTest="disjointness-consistency"
                title="Classes That Are Both Disjoint And Equivalent"
                issues={issues.disjointnessconsistency}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Disjoint classes means that things cannot be members of both classes at the same time.</Typography>
                    <Typography variant="body2" color="textPrimary">When inferences are computed certain structures in the ontology will mean that this disjointness test will fail.</Typography>
                    <Typography variant="body2" color="textPrimary">There are quite few scenarios where this might be the case. More detail on each scenario will be listed below.</Typography>
                    <Typography variant="body2" color="textPrimary">The resolution for these problems will vary, but often it will be the case that there are errors in the class hierarchy and/or classes stated as disjoint may not actually be so.</Typography>
                </div>}
                loadProps={[
                    "id",
                    "disjointnessInconsistency",
                    "subclassDisjointnessInconsistency",
                    "multipleSubclassDisjointnessInconsistency",
                    "disjointnessEquivalenceInconsistency"
                ]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-disjointnessconsistency")}
                onDisable={(disable) => onDisable("disjointnessconsistency", disable)}
                onRefresh={() => onRefresh("disjointnessconsistency")}
                errors={issues["disjointnessconsistency-error"]}
                fromCache={issues["disjointnessconsistency-time"]}
                issueDisplay={
                    (li) => {
                        return consistencyReport.map((lr, lrIndex) =>
                            li[lr.prop] &&
                            <div data-test={"lr-" + lrIndex} key={li.id + "-" + lr.prop} style={{ marginBottom: 10 }}>
                                <Typography data-test="qm" variant="body2" color="textPrimary" paragraph>{lr.message}</Typography>
                                <CommonIriDisplay
                                    id={li.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {li[lr.prop].sort().map(v => {
                                    return <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />;
                                })}
                            </div>
                        );
                    }

                }
            />
            <QualityCheck
                dataTest="symmetric-asymmetric-issues"
                title="Symmetric Properties With Asymmetric Inverses"
                issues={issues.symmetricasymmetricinverses}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Symmetric properties are those where the relationship applies in both directions.</Typography>
                    <Typography variant="body2" color="textPrimary">Asymmetric properties are those where it cannot apply in both directions.</Typography>
                    <Typography variant="body2" color="textPrimary">Where an asymmetric property is an inverse of a symmetric property the asymmetric condition will fail as the relationship will be inferred in both directions.</Typography>
                    <Typography variant="body2" color="textPrimary">The resolution for this is most likely that the asymmetric property is not an inverse of the symmetric property and so you should remove the inverse statement.</Typography>
                </div>}
                loadProps={["id", "symmetricAsymmetricInverse"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-symmetricasymmetricinverses")}
                onDisable={(disable) => onDisable("symmetricasymmetricinverses", disable)}
                onRefresh={() => onRefresh("symmetricasymmetricinverses")}
                errors={issues["symmetricasymmetricinverses-error"]}
                fromCache={issues["symmetricasymmetricinverses-time"]}
                issueDisplay={
                    (cc) => cc.symmetricAsymmetricInverse && [...cc.symmetricAsymmetricInverse].sort().map((v, vIndex) => {
                        return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                            <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            <CommonIriDisplay
                                id={v}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                        </div>;
                    })
                }
            />
            <QualityCheck
                dataTest="reflexive-irreflexive"
                title="Properties That Are Both Reflexive And Irreflexive"
                issues={issues.reflexiveirreflexive}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Reflexive properties are those where the relationship applies to the thing it is on (it points to itself).</Typography>
                    <Typography variant="body2" color="textPrimary">Irreflexive properties are those where it cannot apply to itself.</Typography>
                    <Typography variant="body2" color="textPrimary">A property cannot be both reflexive and irreflexive as this would cause a conflict.</Typography>
                    <Typography variant="body2" color="textPrimary">To resolve this remove one or both of these characteristics.</Typography>
                </div>}
                disabled={disabledFeatures && disabledFeatures.includes("quality-reflexiveirreflexive")}
                onDisable={(disable) => onDisable("reflexiveirreflexive", disable)}
                onRefresh={() => onRefresh("reflexiveirreflexive")}
                errors={issues["reflexiveirreflexive-error"]}
                fromCache={issues["reflexiveirreflexive-time"]}
                issueDisplay={
                    (cc) => cc.conflictingTypes ?
                        <div data-test={"item"} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                        </div>
                        :
                        null
                }
            />
            <QualityCheck
                dataTest="symmetric-asymmetric"
                title="Properties That Are Both Symmetric And Asymmetric"
                issues={issues.symmetricasymmetric}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Symmetric properties are those where the relationship applies in both directions.</Typography>
                    <Typography variant="body2" color="textPrimary">Asymmetric properties are those where it cannot apply in both directions.</Typography>
                    <Typography variant="body2" color="textPrimary">A property cannot be both symmetric and asymmetric as this would cause a conflict.</Typography>
                    <Typography variant="body2" color="textPrimary">To resolve this remove one or both of these characteristics.</Typography>
                </div>}
                disabled={disabledFeatures && disabledFeatures.includes("quality-symmetricasymmetric")}
                onDisable={(disable) => onDisable("symmetricasymmetric", disable)}
                onRefresh={() => onRefresh("symmetricasymmetric")}
                errors={issues["symmetricasymmetric-error"]}
                fromCache={issues["symmetricasymmetric-time"]}
                issueDisplay={
                    (cc) => cc.conflictingTypes ?
                        <div data-test={"item"} style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={cc.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                        </div>
                        :
                        null
                }
            />
            <QualityCheck
                dataTest="equivalent-disjoint"
                title="Properties That Are Both Disjoint And Equivalent"
                issues={issues.equivalentdisjoint}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "disjointnessEquivalenceInconsistency"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Equivalent properties have the same resources. Disjoint properties cannot have the same resources.</Typography>
                    <Typography variant="body2" color="textPrimary">A property cannot be both equivalent and disjoint as this would cause a conflict.</Typography>
                    <Typography variant="body2" color="textPrimary">To resolve this remove one of these relationships.</Typography>
                </div>}
                disabled={disabledFeatures && disabledFeatures.includes("quality-equivalentdisjoint")}
                onDisable={(disable) => onDisable("equivalentdisjoint", disable)}
                onRefresh={() => onRefresh("equivalentdisjoint")}
                errors={issues["equivalentdisjoint-error"]}
                fromCache={issues["equivalentdisjoint-time"]}
                issueDisplay={
                    (dw) => dw.disjointnessEquivalenceInconsistency &&
                        <div style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={dw.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                            <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            {dw.disjointnessEquivalenceInconsistency.sort().map(v =>
                                <CommonIriDisplay
                                    id={v}
                                    key={v}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                            )}
                        </div>
                }
            />
            <QualityCheck
                dataTest="equivalency-inconsistency"
                title="Equivalent Properties of Differing Types"
                issues={issues.equivalencyinconsistency}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "equivalencyinconsistency"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Equivalent properties must be of the same type.</Typography>
                    <Typography variant="body2" color="textPrimary">To resolve this remove the equivalence relationship.</Typography>
                </div>}
                disabled={disabledFeatures && disabledFeatures.includes("quality-equivalencyinconsistency")}
                onDisable={(disable) => onDisable("equivalencyinconsistency", disable)}
                onRefresh={() => onRefresh("equivalencyinconsistency")}
                errors={issues["equivalencyinconsistency-error"]}
                fromCache={issues["equivalencyinconsistency-time"]}
                issueDisplay={
                    (dw) => dw.equivalencyInconsistency &&
                        <div style={{ marginBottom: 10 }}>
                            <CommonIriDisplay
                                id={dw.id}
                                compact={true}
                                projectIRI={getProjectFromGraph(graph)}
                                handleNotAvailable={true}
                                loadComponents={true}
                                graph={graph}
                                wrap={false}
                                onLink={onToggleView}
                                viewLang={this.props.defaultLang}
                                defaultLang={this.props.defaultLang} />
                            <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                            {dw.equivalencyInconsistency.sort().map(v =>
                                <CommonIriDisplay
                                    id={v}
                                    key={v}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                            )}
                        </div>
                }
            />
            <QualityCheck
                dataTest="invalid-equivalency"
                title="Equivalent Property Relationships Where They Should Not Occur"
                issues={issues.invalidequivalentproperty}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "invalidEquivalentProperty"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-invalidequivalentproperty")}
                onDisable={(disable) => onDisable("invalidequivalentproperty", disable)}
                onRefresh={() => onRefresh("invalidequivalentproperty")}
                errors={issues["invalidequivalentproperty-error"]}
                fromCache={issues["invalidequivalentproperty-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Equivalent property relationships should only occur on object or datatype properties and should not target annotation properties.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the relationships. You may need to do this in the triples view.</Typography>
                </div>}
                issueDisplay={
                    (r) => {
                        return r.invalidEquivalentProperty &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={r.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {r.invalidEquivalentProperty.sort().map(v =>
                                    <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                )}
                            </div>;
                    }}
            />
            <QualityCheck
                dataTest="invalid-equivalentclass"
                title="Equivalent Class Relationships Where They Should Not Occur"
                issues={issues.invalidequivalentclass}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "invalidEquivalentClass"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-invalidequivalentclass")}
                onDisable={(disable) => onDisable("invalidequivalentclass", disable)}
                onRefresh={() => onRefresh("invalidequivalentclass")}
                errors={issues["invalidequivalentclass-error"]}
                fromCache={issues["invalidequivalentclass-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Equivalent class relationships should only occur on classes.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the relationships. You may need to do this in the triples view.</Typography>
                </div>}
                issueDisplay={
                    (r) => {
                        return r.invalidEquivalentClass &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={r.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {r.invalidEquivalentClass.sort().map(v =>
                                    <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                )}
                            </div>;
                    }}
            />
            <QualityCheck
                dataTest="disjoint-individuals"
                title="Disjoint Classes On The Same Named Individual"
                issues={issues.disjointindividuals}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "disjointIndividualClassViolation"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-disjointindividuals")}
                onDisable={(disable) => onDisable("disjointindividuals", disable)}
                onRefresh={() => onRefresh("disjointindividuals")}
                errors={issues["disjointindividuals-error"]}
                fromCache={issues["disjointindividuals-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Disjoint classes means that no individual should have both classes, as it is not possible to be both classes at the same time.</Typography>
                    <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the classes on each individual, or checking whether the classes should actually be disjoint.</Typography>
                </div>}
                issueDisplay={
                    (dw) => {
                        return dw.disjointIndividualClassViolation &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={dw.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {dw.disjointIndividualClassViolation.sort().map(v =>
                                    <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                )}
                            </div>;
                    }}
            />
            <QualityCheck
                dataTest="different-sameas-individuals"
                title="Named Individuals That Are Both Different From and Same As"
                issues={issues.differentfromsameas}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "differentFromSameAsViolation"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-differentfromsameas")}
                onDisable={(disable) => onDisable("differentfromsameas", disable)}
                onRefresh={() => onRefresh("differentfromsameas")}
                errors={issues["differentfromsameas-error"]}
                fromCache={issues["differentfromsameas-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">A pair of Named Individuals cannot be both the same thing and different things at the same time.</Typography>
                    <Typography variant="body2" color="textPrimary">You can possibly resolve these issues by removing one of the different from or same as relationships.</Typography>
                </div>}
                issueDisplay={
                    (ni) => {
                        return ni.differentFromSameAsViolation &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={ni.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {ni.differentFromSameAsViolation.sort().map(v =>
                                    <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                )}
                            </div>;
                    }}
            />
            <QualityCheck
                dataTest="invalid-sameas-individuals"
                title="Same As Between Things That Are Not Named Individuals"
                issues={issues.invalidsameas}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "invalidSameAs"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-invalidsameas")}
                onDisable={(disable) => onDisable("invalidsameas", disable)}
                onRefresh={() => onRefresh("invalidsameas")}
                errors={issues["invalidsameas-error"]}
                fromCache={issues["invalidsameas-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Same as relationships should only occur between named individuals.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the same as relationships. You may need to do this in the triples view.</Typography>
                </div>}
                issueDisplay={
                    (ni) => {
                        return ni.invalidSameAs &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={ni.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {ni.invalidSameAs.sort().map(v =>
                                    <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                )}
                            </div>;
                    }}
            />
            <QualityCheck
                dataTest="invalid-differentfrom-individuals"
                title="Different From Between Things That Are Not Named Individuals"
                issues={issues.invaliddifferentfrom}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id", "invalidDifferentFrom"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-invaliddifferentfrom")}
                onDisable={(disable) => onDisable("invaliddifferentfrom", disable)}
                onRefresh={() => onRefresh("invaliddifferentfrom")}
                errors={issues["invaliddifferentfrom-error"]}
                fromCache={issues["invaliddifferentfrom-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Different from relationships should only occur between named individuals.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by removing the different from relationships. You may need to do this in the triples view.</Typography>
                </div>}
                issueDisplay={
                    (ni) => {
                        return ni.invalidDifferentFrom &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={ni.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                                <ArrowLeftIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                {ni.invalidDifferentFrom.sort().map(v =>
                                    <CommonIriDisplay
                                        id={v}
                                        key={v}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                )}
                            </div>;
                    }}
            />
            {this.state.filteredViews.includes("invalidifp") &&
                <QualityCheck
                    dataTest="invalid-ifp"
                    title="Inverse Functional Property Definitions Where Not Permitted"
                    issues={issues.invalidifp}
                    isLoading={isLoading}
                    graph={graph}
                    isReadOnly={isReadOnly}
                    defaultLang={defaultLang}
                    loadProps={["id"]}
                    disabled={disabledFeatures && disabledFeatures.includes("quality-invalidifp")}
                    onDisable={(disable) => onDisable("invalidifp", disable)}
                    onRefresh={() => onRefresh("invalidifp")}
                    errors={issues["invalidifp-error"]}
                    fromCache={issues["invalidifp-time"]}
                    meaning={<div>
                        <Typography variant="body2" color="textPrimary">Inverse functional properties can only be on object properties.</Typography>
                        <Typography variant="body2" color="textPrimary">You can resolve these issues by unchecking the inverse functional characteristic.</Typography>
                    </div>}
                    issueDisplay={
                        (r) => {
                            return r.invalidIFP &&
                                <div style={{ marginBottom: 10 }}>
                                    <CommonIriDisplay
                                        id={r.id}
                                        compact={true}
                                        projectIRI={getProjectFromGraph(graph)}
                                        handleNotAvailable={true}
                                        loadComponents={true}
                                        graph={graph}
                                        wrap={false}
                                        onLink={onToggleView}
                                        viewLang={this.props.defaultLang}
                                        defaultLang={this.props.defaultLang} />
                                </div>;
                        }}
                />
            }
            <QualityCheck
                dataTest="incomplete-restriction"
                title="Incomplete Restrictions and Expressions"
                issues={issues.incompleterestriction}
                isLoading={isLoading}
                graph={graph}
                isReadOnly={isReadOnly}
                defaultLang={defaultLang}
                loadProps={["id"]}
                disabled={disabledFeatures && disabledFeatures.includes("quality-incompleterestriction")}
                onDisable={(disable) => onDisable("incompleterestriction", disable)}
                onRefresh={() => onRefresh("incompleterestriction")}
                errors={issues["incompleterestriction-error"]}
                fromCache={issues["incompleterestriction-time"]}
                meaning={<div>
                    <Typography variant="body2" color="textPrimary">Incomplete restrictions or expressions imply that the ontology is not finished.</Typography>
                    <Typography variant="body2" color="textPrimary">You can resolve these issues by completing the restrictions or expressions.</Typography>
                </div>}
                issueDisplay={
                    (r) => {
                        return r.incompleteRestriction &&
                            <div style={{ marginBottom: 10 }}>
                                <CommonIriDisplay
                                    id={r.id}
                                    compact={true}
                                    projectIRI={getProjectFromGraph(graph)}
                                    handleNotAvailable={true}
                                    loadComponents={true}
                                    graph={graph}
                                    wrap={false}
                                    onLink={onToggleView}
                                    viewLang={this.props.defaultLang}
                                    defaultLang={this.props.defaultLang} />
                            </div>;
                    }}
            />
        </div>;
    };

    render() {

        const { filteredViews } = this.state;
        const { id, graph, data } = this.props;

        return (
            <div>
                <QualityCheckLoader
                    id={id}
                    graph={graph}
                    disabledFeatures={data.disabledFeatures}
                    displayResults={this.displayResults}
                    views={filteredViews} />
            </div>
        );
    }

}

OntologyQuality.propTypes = {
    onToggleView: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    onDisable: PropTypes.func.isRequired,
    defaultLang: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    history: PropTypes.object,
    graph: PropTypes.string.isRequired,
    entitlement: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withRouter(OntologyQuality));